/**
 * RegisterTemporary page
 */

// React
import React, { FC } from 'react';

// Material
import { Typography, Link } from '@mui/material';

// Validation
import { useRegisterTem } from './useRegisterTem';
import {
  FieldContainer,
  FieldErrorContainer,
  FormButton,
  FormContainer,
  TypographyCustom,
  WrapperContent,
  WrapperForm,
  WrapperInputOrMessage,
} from 'styles';

// Hook
import AppInput from 'components/common/AppInput';

// Constants
import {
  DATE_TIME_FORMATS,
  FIELD_NAMES,
  FORM_BUTTON,
  FORM_PLACEHOLDER,
  FORM_REGISTER_TEMPORARY_TITLE_HEADER,
  FORM_REGISTER_TEMPORARY_TITLE_HEADER_SUCCESS,
  FORM_TOOLTIP,
  URL_PRIVACY_TERM,
} from 'utils/constants';

// Utils
import { convertTimestampToDateTime, parseMessage } from 'utils/helper';

// Form fields
const formRegisterTem = [
  {
    name: FIELD_NAMES.EMAIL,
    placeholder: FORM_PLACEHOLDER.EMAIL_REGISTER_TEMPORARY,
    type: 'email',
  },
  {
    name: FIELD_NAMES.EMAIL_CONFIRMATION,
    placeholder: FORM_PLACEHOLDER.EMAIL_REGISTER_TEMPORARY_CONFIRMATION,
    type: 'email',
    titleTooltip: FORM_TOOLTIP.EMAIL_CONFIRM,
  },
];

// RegisterTemporary component
const RegisterTemporary: FC = () => {
  const {
    control,
    handleSubmit,
    errors,
    isRegisterEmailSuccess,
    handleBackPreviousPage,
    onSubmit,
    expiredTime,
    errorMessage,
  } = useRegisterTem();

  const expiredTimeWithFormat = expiredTime
    ? convertTimestampToDateTime(+expiredTime, DATE_TIME_FORMATS.JAPANESE)
    : '〜〜〜';
  const { HEADING, DESCRIPTIONS } = isRegisterEmailSuccess
    ? FORM_REGISTER_TEMPORARY_TITLE_HEADER_SUCCESS
    : FORM_REGISTER_TEMPORARY_TITLE_HEADER;

  const renderFormRegisterTem = () => {
    return (
      <WrapperForm>
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <WrapperContent alignItems="start">
            <TypographyCustom variant="h2">{HEADING}</TypographyCustom>
            {DESCRIPTIONS?.map((description, index) => (
              <Typography key={index} variant="h3">
                {description}
              </Typography>
            ))}
          </WrapperContent>

          {formRegisterTem.map(({ name, placeholder, type, titleTooltip }) => (
            <FieldContainer key={name}>
              <AppInput
                name={name}
                control={control}
                type={type}
                errors={errors}
                titleTooltip={titleTooltip}
                helperText={errors?.[name]?.message}
                placeholder={placeholder}
              />
            </FieldContainer>
          ))}

          {errorMessage && (
            <FieldErrorContainer>
              <WrapperInputOrMessage sx={{ width: 'max-content' }}>
                <Typography variant="h4" fontWeight="bold" textAlign="start">
                  {errorMessage}
                </Typography>
              </WrapperInputOrMessage>
            </FieldErrorContainer>
          )}

          <WrapperContent alignItems="center">
            <Link
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              tabIndex={-1}
              onClick={(e) => {
                e.preventDefault();
                window.open(URL_PRIVACY_TERM.PRIVACY_ACCOUNT_REGISTRATION_URL, '_blank');
              }}
            >
              {FORM_BUTTON.PRIVACY_POLICY}
            </Link>
          </WrapperContent>

          <FormButton
            variant="contained"
            type="submit"
            color="secondary"
            sx={{
              width: '100%',
              maxWidth: '400px',
              whiteSpace: 'normal',
              lineHeight: 1.3,
              py: 1.5,
            }}
          >
            {FORM_BUTTON.SEND_WITH_PRIVACY_CONSENT}
          </FormButton>
        </FormContainer>
      </WrapperForm>
    );
  };

  const renderRegisterEmailSuccess = () => {
    return (
      <WrapperForm>
        <FormContainer>
          <WrapperContent alignItems="center">
            <TypographyCustom variant="h2">{HEADING}</TypographyCustom>
            {DESCRIPTIONS?.map((description, index) => (
              <Typography key={index} variant="h3">
                {parseMessage(description, [expiredTimeWithFormat])}
              </Typography>
            ))}
          </WrapperContent>
          <FormButton
            onClick={handleBackPreviousPage}
            variant="contained"
            type="button"
            color="secondary"
          >
            {FORM_BUTTON.BACK}
          </FormButton>
        </FormContainer>
      </WrapperForm>
    );
  };

  return isRegisterEmailSuccess ? renderRegisterEmailSuccess() : renderFormRegisterTem();
};

export default RegisterTemporary;
